import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@cocoonspace/ui/components/button'
import { Form } from '@cocoonspace/ui/components/form'
import { FormControlledInput } from '@cocoonspace/ui/components/form-controlled-input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@cocoonspace/ui/components/select'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLingui } from '@lingui/react/macro'
import z from 'zod'

import { useGetFormattedDuration } from './use-get-formatted-duration'

const schema = z.object({
	start: z.string(),
	end: z.string(),
})

type EditBookingWindowFormData = z.infer<typeof schema>

const useGetTimeEntries = () => {
	const durations = useGetFormattedDuration()

	return useMemo(
		() =>
			Object.entries(durations).map(([value, label]) => ({
				value,
				label,
			})),
		[durations],
	)
}

const useSchema = () => {
	const durations = useGetFormattedDuration()

	return useMemo(() => {
		const durationOrder = Object.keys(durations)

		return schema.superRefine((data, ctx) => {
			const startIndex = durationOrder.indexOf(data.start)
			const endIndex = durationOrder.indexOf(data.end)

			if (startIndex >= endIndex) {
				ctx.addIssue({
					code: 'custom',
					params: { customCode: 'endTimeMustBeHigherThanStartTime' },
					path: ['start'],
				})
			}
		})
	}, [durations])
}

export const BookingWindowForm = ({
	bookingWindow,
	onSubmit,
	onClose,
}: {
	bookingWindow: EditBookingWindowFormData
	onSubmit: (data: EditBookingWindowFormData) => void
	onClose?: VoidFunction
}) => {
	const { t } = useLingui()

	const timeEntries = useGetTimeEntries()
	const enhancedSchema = useSchema()

	const form = useForm<EditBookingWindowFormData>({
		resolver: zodResolver(enhancedSchema),
		defaultValues: bookingWindow,
	})

	const entries = useMemo(
		() =>
			[
				{ label: t`Début`, name: 'start', placeholder: t`Date de début` },
				{ label: t`Fin`, name: 'end', placeholder: t`Date de fin` },
			] as const,
		[t],
	)

	return (
		<div>
			<Form {...form}>
				<form>
					{entries.map(({ label, name, placeholder }) => (
						<FormControlledInput
							key={name}
							label={label}
							control={form.control}
							name={name}
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value)
										form.trigger(['start'])
									}}
								>
									<SelectTrigger inputSize='sm'>
										<SelectValue placeholder={placeholder} />
									</SelectTrigger>

									<SelectContent>
										{timeEntries.map(({ value, label }) => (
											<SelectItem
												value={value}
												key={value}
											>
												{label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						/>
					))}

					<div className='flex justify-end gap-x-4'>
						<Button
							size='sm'
							variant='link'
							onClick={onClose}
						>
							{t`Annuler`}
						</Button>

						<Button
							size='sm'
							onClick={form.handleSubmit(onSubmit)}
						>
							{t`Valider`}
						</Button>
					</div>
				</form>
			</Form>
		</div>
	)
}

export type { EditBookingWindowFormData }
